import "./App.css";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material";
import { useEffect } from "react";

// Consolidated font imports
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/500.css";
import "@fontsource/montserrat/600.css";
import "@fontsource/montserrat/700.css";
import "@fontsource/montserrat/400-italic.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/400-italic.css";

// Theme
import { MuiTheme } from "./assets/theme";

// Layouts
import MainPageLayout from "./layout/MainPageLayout";
import ProtectedLayout from "./layout/ProtectedLayout";
import ProtectedRoute from "./components/ProtectedRoute";

// Context
import UserProvider from "./context/UserContext";
import AuthProvider from "./context/AuthContext";
import NotificationProvider from "./context/NotificationContext";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage.jsx";
import SignupPage from "./pages/SignupPage.jsx";
import ForgotPasswordPage from "./pages/ForgotPassword.jsx";
import DashboardPage from "./pages/DashboardPage";
import LandlordHomePage from "./pages/LandlordHomePage";
import DraftJobsPage from "./pages/DraftJobsPage";
import SavedJobsPage from "./pages/SavedJobsPage";
import PropertiesPage from "./pages/PropertiesPage";
import PostJobPage from "./pages/PostJobPage";
import ActiveJobsPage from "./pages/ActiveJobsPage";
import AvailableJobsPage from "./pages/AvailableJobsPage";
import PendingApprovalsPage from "./pages/PendingApprovalsPage";
import PastJobsPage from "./pages/PastJobsPage";
import InvoicesPage from "./pages/OpenInvoices.jsx";
import PaidInvoicesPage from "./pages/PaidInvoices.jsx";
import TrackPaymentsPage from "./pages/TrackPaymentsPage";
import TransactionHistoryPage from "./pages/TransactionHistoryPage";
import MessagesPage from "./pages/MessagesPage";
import WarrantyServicePage from "./pages/WarrantyService";
import WarrantyClaimReviewPage from "./pages/WarrantyClaimReview";
import PaymentMethodsPage from "./pages/PaymentMethodsPage";

import VendorOnboardingPage from "./pages/VendorOnboarding";
import { CurrentSelectedDataProvider } from "./context/CurrentSelectedDataContext.jsx";
// Landing Pages
import HomeownerLandingPage from "./components/HomeownerLandingPage";
import VendorLandingPage from "./components/VendorLandingPage";
import LandlordLandingPage from "./components/LandlordLandingPage";
import PropertyManagersLandingPage from "./components/PropertyManagersLandingPage";
import ShortTermRentalsLandingPage from "./components/ShortTermRentalsLandingPage";
import { StatisticsDataProvider } from "./context/StatisticsData.jsx";
import PostSignupThankYouPage from "./pages/PostSignupThankYou.jsx";
import SettingsPage from "./pages/SettingsPage.jsx";
import BlogListPage from "./pages/BlogListPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import LegalPage from "./pages/LegalPage";
import LeadMagnetPage from "./pages/LeadMagnetPage";
import LeadMagnetThankYou from "./pages/LeadMagnetThankYou";
import LeadMagnetDownload from "./pages/LeadMagnetDownload";
import HomeMaintenanceAssessmentPage from "./pages/HomeMaintenanceAssessmentPage";
import HomeMaintenanceAssessmentThankYou from "./pages/HomeMaintenanceAssessmentThankYou";
// Route configurations
const authRoutes = [
  { path: "/login", element: <LoginPage /> },
  { path: "/signup", element: <SignupPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/post-signup-thank-you", element: <PostSignupThankYouPage /> },
];

const publicRoutes = [
  { path: "/", element: <HomePage /> },
  { path: "/landlords", element: <LandlordLandingPage /> },
  { path: "/property-managers", element: <PropertyManagersLandingPage /> },
  { path: "/short-term-rentals", element: <ShortTermRentalsLandingPage /> },
  { path: "/homeowners", element: <HomeownerLandingPage /> },
  { path: "/vendors", element: <VendorLandingPage /> },
  { path: "/blog", element: <BlogListPage /> },
  { path: "/blog/:slug", element: <BlogDetailPage /> },
  { path: "/legal", element: <LegalPage /> },
];

const protectedRoutes = [
  { path: "/dashboard", element: <DashboardPage /> },
  { path: "/landlord-home", element: <LandlordHomePage /> },
  { path: "/draft-jobs", element: <DraftJobsPage /> },
  { path: "/properties", element: <PropertiesPage /> },
  { path: "/saved-jobs", element: <SavedJobsPage /> },
  { path: "/active-jobs", element: <ActiveJobsPage /> },
  { path: "/available-jobs", element: <AvailableJobsPage /> },
  { path: "/pending-approvals", element: <PendingApprovalsPage /> },
  { path: "/post-job", element: <PostJobPage /> },
  { path: "/open-invoices", element: <InvoicesPage /> },
  { path: "/paid-invoices", element: <PaidInvoicesPage /> },
  { path: "/track-payments", element: <TrackPaymentsPage /> },
  { path: "/transaction-history", element: <TransactionHistoryPage /> },
  { path: "/messages", element: <MessagesPage /> },
  { path: "/warranty-service/:jobId", element: <WarrantyServicePage /> },
  { path: "/warranty-claims/:claimId", element: <WarrantyClaimReviewPage /> },

  { path: "/vendor-onboarding", element: <VendorOnboardingPage /> },
  // Alias to support backend redirect path
  { path: "/vendor/onboarding", element: <VendorOnboardingPage /> },
  { path: "/past-jobs", element: <PastJobsPage /> },
  { path: "/settings", element: <SettingsPage /> },
  { path: "/payment-methods", element: <PaymentMethodsPage /> },
];

// Helper component for auth routes
const AuthRouteWrapper = ({ children }) => (
  <>{children}</>
);

// Helper component for protected routes
const ProtectedRouteWrapper = ({ children }) => (
  <ProtectedRoute>
    <ProtectedLayout />
  </ProtectedRoute>
);

function App() {
  const theme = createTheme(MuiTheme);

  // Global error handler to prevent {isTrusted: true} WebSocket errors from showing in console
  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      // Filter out WebSocket error objects that only contain isTrusted
      const filteredArgs = args.filter((arg) => {
        if (typeof arg === "object" && arg !== null) {
          const keys = Object.keys(arg);
          if (keys.length === 1 && keys[0] === "isTrusted") {
            return false; // Don't log this
          }
        }
        return true;
      });

      if (filteredArgs.length > 0) {
        originalConsoleError.apply(console, filteredArgs);
      }
    };

    // Cleanup function to restore original console.error
    return () => {
      console.error = originalConsoleError;
    };
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>} showDialog>
    <CurrentSelectedDataProvider>
      <StatisticsDataProvider>
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <AuthProvider>
              <UserProvider>
                <NotificationProvider>
                  <Routes>
                    {/* Auth Routes - No navbar/footer */}
                    {authRoutes.map(({ path, element }) => (
                      <Route
                        key={path}
                        path={path}
                        element={<AuthRouteWrapper>{element}</AuthRouteWrapper>}
                      />
                    ))}

                    {/* Lead Magnet funnel - Standalone, no navbar/footer, noindex */}
                    <Route path="/free-ebook" element={<LeadMagnetPage />} />
                    <Route path="/free-ebook/thank-you" element={<LeadMagnetThankYou />} />
                    <Route path="/free-ebook/download" element={<LeadMagnetDownload />} />
                    <Route path="/home-maintenance-assessment" element={<HomeMaintenanceAssessmentPage />} />
                    <Route
                      path="/home-maintenance-assessment/thank-you"
                      element={<HomeMaintenanceAssessmentThankYou />}
                    />

                    {/* Public Routes with Navbar and Footer */}
                    <Route path="/" element={<MainPageLayout />}>
                      {publicRoutes.map(({ path, element }) => (
                        <Route key={path} path={path} element={element} />
                      ))}
                    </Route>

                    {/* Protected Routes with Navbar and Footer */}
                    {protectedRoutes.map(({ path, element }) => (
                      <Route
                        key={path}
                        path={path}
                        element={
                          <ProtectedRouteWrapper>{element}</ProtectedRouteWrapper>
                        }
                      >
                        <Route index element={element} />
                      </Route>
                    ))}
                  </Routes>
                </NotificationProvider>
              </UserProvider>
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </StatisticsDataProvider>
    </CurrentSelectedDataProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
